/**
 * Verbesserungsvorschläge – Liste.
 *
 * Eine Zeile je Vorschlag, zum Überfliegen: Prioritätspunkt, Kennung, Titel,
 * Bereich, Status, Ersteller mit Datum. Die Beschreibung steht bewusst nicht
 * hier – sonst sähe jede Zeile gleich aus und man müsste fünf Absätze lesen,
 * um zu wissen, worum es geht.
 *
 * Sortiert wird chronologisch, neueste zuerst – nicht nach Priorität. Sonst
 * verschwinden neue Vorschläge mit niedriger Priorität ganz unten und werden
 * nie beantwortet.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { useKora } from '../zustand';
import { VorschlagModal } from '../modals/VorschlagModal';
import type { VorschlagListe, VorschlagZeile } from '../typen';
import './vorschlaege.css';
import { Menueknopf } from '../komponenten/Shell';

const STATUS_REIHE = ['Eingebracht', 'Genehmigt', 'Umgesetzt', 'Abgelehnt'];

/** Kurzes Datum wie im Design: „30. Aug". */
export function datKurz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-AT', { day: 'numeric', month: 'short' });
}

export function Vorschlaege() {
  const navigate = useNavigate();
  const { nutzer } = useKora();
  const [suchParameter, setSuchParameter] = useSearchParams();
  const [anlegen, setAnlegen] = useState(false);

  const filter = suchParameter.get('status') ?? '';

  const abfrage = useQuery({
    queryKey: ['vorschlaege'],
    queryFn: () => api.hole<VorschlagListe>('/vorschlaege'),
  });

  const alle = useMemo(() => abfrage.data?.vorschlaege ?? [], [abfrage.data]);
  const liste = filter ? alle.filter((v) => v.status === filter) : alle;

  const zaehler = useMemo(() => {
    const k = new Map<string, number>();
    for (const v of alle) k.set(v.status, (k.get(v.status) ?? 0) + 1);
    return k;
  }, [alle]);

  const eigene = alle.filter((v) => v.ersteller === nutzer?.anzeigename).length;

  function oeffnen(v: VorschlagZeile) {
    // Der Filter reist mit, damit „← Zurück" wieder dieselbe Auswahl zeigt.
    navigate(`/vorschlaege/${v.id}`, {
      state: { zurueck: `/vorschlaege${filter ? `?status=${encodeURIComponent(filter)}` : ''}` },
    });
  }

  function filternAuf(wert: string) {
    if (wert) setSuchParameter({ status: wert });
    else setSuchParameter({});
  }

  return (
    <div className="eh-inhalt vv">
      <div className="vv-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
          <div className="vv-eyebrow">Kora verbessern</div>
          <h1 className="vv-titel">Verbesserungsvorschläge</h1>
        </div>
        </div>
        <div className="eh-kopf-rechts">
          <Nachrichtenknopf />
          <button type="button" className="eh-knopf-primaer vv-neu" onClick={() => setAnlegen(true)}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            <span>Vorschlag einbringen</span>
          </button>
        </div>
      </div>

      <div className="vv-filter">
        <div className="vv-chips">
          <Chip
            label="Alle"
            anzahl={alle.length}
            aktiv={filter === ''}
            onClick={() => filternAuf('')}
          />
          {STATUS_REIHE.map((s) => (
            <Chip
              key={s}
              label={s}
              anzahl={zaehler.get(s) ?? 0}
              aktiv={filter === s}
              onClick={() => filternAuf(s)}
            />
          ))}
        </div>
        <div className="vv-eigenhinweis">
          {eigene === 0
            ? 'Du hast noch keinen Vorschlag eingebracht.'
            : `Du hast ${eigene === 1 ? 'einen Vorschlag' : `${eigene} Vorschläge`} eingebracht.`}
        </div>
      </div>

      <div className="eh-karte vv-tabelle">
        <div className="vv-zeile vv-zeile--kopf">
          <div>Vorschlag</div>
          <div>Bereich</div>
          <div>Status</div>
          <div>Eingebracht</div>
          <div />
        </div>

        {abfrage.isLoading && <div className="vv-leer">Wird geladen …</div>}

        {!abfrage.isLoading && liste.length === 0 && (
          <div className="vv-leer">
            {alle.length === 0
              ? 'Noch kein Vorschlag eingebracht. Der erste darf deiner sein.'
              : 'Kein Vorschlag mit diesem Status.'}
          </div>
        )}

        {liste.map((v) => (
          <button key={v.id} type="button" className="vv-zeile" onClick={() => oeffnen(v)}>
            <div className="vv-sache">
              <span className={`vv-punkt vv-punkt--${v.prioritaet}`} title={`Priorität ${v.prioritaet}`} />
              <span className="vv-kennung">{v.kennung}</span>
              <span className="vv-zeilentitel">{v.titel}</span>
            </div>
            <div className="vv-bereich" title={v.bereichLabel}>
              {v.bereichLabel}
            </div>
            <div>
              <StatusPille status={v.status} />
            </div>
            <div className="vv-wer">
              <span className="eh-avatar vv-avatar">{v.erstellerInitialen}</span>
              <span className="vv-datum">{datKurz(v.angelegtAm)}</span>
            </div>
            <div className="vv-chevron">›</div>
          </button>
        ))}
      </div>

      {anlegen && (
        <VorschlagModal
          bereiche={abfrage.data?.bereiche ?? []}
          onSchliessen={() => setAnlegen(false)}
        />
      )}
    </div>
  );
}

function Chip({
  label,
  anzahl,
  aktiv,
  onClick,
}: {
  label: string;
  anzahl: number;
  aktiv: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`vv-chip ${aktiv ? 'vv-chip--aktiv' : ''}`} onClick={onClick}>
      {label}
      <span className="vv-chip-zahl">{anzahl}</span>
    </button>
  );
}

/**
 * Vier Status mit klarer Farbsemantik. Abgelehnt ist bewusst nicht rot: Rot ist
 * in Kora Handlungs- und Eskalationsfarbe, eine sachliche Absage soll nicht
 * schreien.
 */
export function StatusPille({ status }: { status: string }) {
  const schluessel = status.toLowerCase().replace('ä', 'ae').replace('ü', 'ue');
  return <span className={`vv-status vv-status--${schluessel}`}>{status}</span>;
}